import React, { useMemo, useState, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { CalendarIcon, ClockIcon, CheckCircle2Icon, ArrowRightIcon, ArrowLeftIcon, Loader2Icon, ChevronLeftIcon, ChevronRightIcon, UserIcon, MailIcon, PhoneIcon, BuildingIcon, InfoIcon } from 'lucide-react';
const MAIL_ENDPOINT = 'https://scan.noorddigital.nl/mail.php';

// LET OP
// Hier stond eerder een functie die met een random generator twee tot vier
// tijdslots per dag als "Bezet" toonde. Die boekingen bestonden niet.
// Kunstmatige schaarste is een misleidende handelspraktijk en is verwijderd.
// Zolang er geen echte agendakoppeling is, tonen we alle tijden als
// beschikbaar en bevestigen we de afspraak handmatig.

const AVAILABLE_DAYS = [3, 4, 5, 6]; // woensdag tot en met zaterdag
const TIME_SLOTS = ['09:00', '09:45', '10:30', '11:15', '12:00', '12:45', '13:30', '14:15', '15:00', '15:45', '16:15'];
const MONTHS_NL = ['Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni', 'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'];
const WEEKDAYS_NL = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];
const WEEKDAYS_FULL = ['Zondag', 'Maandag', 'Dinsdag', 'Woensdag', 'Donderdag', 'Vrijdag', 'Zaterdag'];
async function sendViaPHPMailer(params: Record<string, string>) {
  const body = new URLSearchParams(params);
  const res = await fetch(MAIL_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    },
    body: body.toString()
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}
function daysInMonth(y: number, m: number) {
  return new Date(y, m + 1, 0).getDate();
}
function toMondayIndex(d: number) {
  return (d + 6) % 7;
}
function startOfDay(d: Date) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function isPast(d: Date) {
  return startOfDay(d) < startOfDay(new Date());
}
function isToday(d: Date) {
  return startOfDay(d).getTime() === startOfDay(new Date()).getTime();
}
function sameDay(a: Date | null, b: Date | null) {
  return !!a && !!b && startOfDay(a).getTime() === startOfDay(b).getTime();
}
function formatLong(d: Date) {
  return `${WEEKDAYS_FULL[d.getDay()]} ${d.getDate()} ${MONTHS_NL[d.getMonth()].toLowerCase()} ${d.getFullYear()}`;
}
function formatShort(d: Date) {
  return `${d.getDate()} ${MONTHS_NL[d.getMonth()].toLowerCase()}`;
}
type Step = 'pick' | 'details' | 'success';
export function Booking() {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [step, setStep] = useState<Step>('pick');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: ''
  });
  const calendarRows = useMemo(() => {
    const first = new Date(viewYear, viewMonth, 1);
    const total = daysInMonth(viewYear, viewMonth);
    const startCol = toMondayIndex(first.getDay());
    const cells: ({
      day: number;
      date: Date;
    } | null)[] = [];
    for (let i = 0; i < startCol; i++) cells.push(null);
    for (let d = 1; d <= total; d++) cells.push({
      day: d,
      date: new Date(viewYear, viewMonth, d)
    });
    while (cells.length % 7 !== 0) cells.push(null);
    const rows: (typeof cells)[] = [];
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
    return rows;
  }, [viewYear, viewMonth]);
  const canGoPrev = !(viewYear === today.getFullYear() && viewMonth === today.getMonth());
  function prevMonth() {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else setViewMonth(viewMonth - 1);
  }
  function nextMonth() {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else setViewMonth(viewMonth + 1);
  }
  function isAvailable(d: Date) {
    return !isPast(d) && AVAILABLE_DAYS.includes(d.getDay());
  }
  function selectDate(d: Date) {
    if (!isAvailable(d)) return;
    setSelectedDate(d);
    setSelectedTime(null);
  }
  function handleFormChange(e: React.ChangeEvent<HTMLInputElement>) {
    setForm({
      ...form,
      [e.target.name]: e.target.value
    });
  }
  function validate() {
    if (!form.name.trim()) return 'Vul je naam in.';
    if (!form.email.trim()) return 'Vul je e-mailadres in.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return 'Vul een geldig e-mailadres in.';
    if (!form.phone.trim()) return 'Vul je telefoonnummer in.';
    return null;
  }
  async function handleSubmit() {
    const err = validate();
    if (err) {
      setErrorMsg(err);
      return;
    }
    if (!selectedDate || !selectedTime) return;
    setErrorMsg(null);
    setSubmitting(true);
    const dateLong = formatLong(selectedDate);
    try {
      await sendViaPHPMailer({
        naam: form.name.trim(),
        email: form.email.trim(),
        telefoon: form.phone.trim(),
        bericht: `Nieuwe CorePrint-aanvraag\n\nBedrijf: ${form.company.trim() || 'Niet opgegeven'}\nVoorkeursdatum: ${dateLong}\nVoorkeurstijd: ${selectedTime} (45 min)\n\nLet op: dit is een voorkeur, nog te bevestigen richting de klant.`,
        ontvanger: 'info@noorddigital.nl',
        onderwerp: `Afspraakverzoek CorePrint — ${form.name.trim()}`
      });
      await sendViaPHPMailer({
        naam: 'Noord Digital',
        email: 'info@noorddigital.nl',
        telefoon: '+31 6 53689686',
        bericht: `Hoi ${form.name.trim()},\n\nBedankt voor je aanvraag. We hebben je voorkeur ontvangen:\n\nDatum: ${dateLong}\nTijd: ${selectedTime} (45 minuten)\nOnline via Google Meet\n\nWe bevestigen dit moment binnen één werkdag per mail. Mocht het onverhoopt niet uitkomen, dan stellen we een alternatief voor.\n\nVragen? Mail info@noorddigital.nl of bel 06 53689686.\n\nMet vriendelijke groet,\nHet Noord Digital team`,
        ontvanger: form.email.trim(),
        onderwerp: `Aanvraag ontvangen: CorePrint-presentatie op ${dateLong}`
      });
      setStep('success');
    } catch (e: any) {
      setErrorMsg('Versturen mislukt. Probeer opnieuw of mail info@noorddigital.nl.');
    } finally {
      setSubmitting(false);
    }
  }
  function resetAll() {
    setStep('pick');
    setSelectedDate(null);
    setSelectedTime(null);
    setForm({
      name: '',
      email: '',
      phone: '',
      company: ''
    });
    setErrorMsg(null);
  }
  return <section id="gesprek" ref={ref} className="py-24 relative overflow-hidden" style={{
    background: 'linear-gradient(160deg, #0D1628 0%, #1B2B44 100%)'
  }} data-id="element-16" data-mp="bh8l1l1436wd">
      <div className="absolute inset-0 grid-bg opacity-30" data-id="element-17" data-mp="kdzmye2chfyv" />

      <div className="relative z-10 max-w-[1100px] mx-auto px-6" data-id="element-18" data-mp="2i6wv47ifj14b">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-16 items-center" data-id="element-19" data-mp="2m770pj2b7pin">
          {/* LEFT */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5
        }} data-id="element-20" data-mp="2eno1c28rdjnw">
            <div className="section-label-dark" data-id="element-21" data-mp="29veckpjdqpn7">Kennismaking</div>
            <h2 className="text-[clamp(2rem,3.6vw,2.8rem)] font-bold text-white leading-tight tracking-tight mb-6" data-id="element-22" data-mp="24ynyu8v1s64y">
              Kijk eerst of het bij je past
            </h2>
            <p className="text-white/60 text-[1.05rem] leading-relaxed mb-10" data-id="element-23" data-mp="2xn1volaegwex">
              In vijfenveertig minuten laten we zien hoe de aanpak werkt, wat het
              in jouw markt zou kosten en wat het rapport straks bevat. Je zit
              daarna tot niets verplicht.
            </p>
            <div className="space-y-4 mb-10" data-id="element-24" data-mp="2jntz805mg2ef">
              {[{
              icon: ClockIcon,
              text: '45 minuten, online via Google Meet'
            }, {
              icon: CheckCircle2Icon,
              text: 'Vrijblijvend, je krijgt de doorrekening mee'
            }, {
              icon: CheckCircle2Icon,
              text: 'Vraag gerust om een voorbeeldrapport'
            }, {
              icon: CalendarIcon,
              text: 'Woensdag tot en met zaterdag, 09:00 tot 17:00'
            }].map(item => <div key={item.text} className="flex items-center gap-4" data-id="element-25" data-mp="2ybxdckges6m8">
                  <item.icon size={18} className="text-brand-arctic flex-shrink-0" data-id="element-26" data-mp="2ydrz20zi82rd" />
                  <span className="text-white/70 text-[0.98rem]" data-id="element-27" data-mp="33bk29r5lnbav">{item.text}</span>
                </div>)}
            </div>

            {/* Marktexclusiviteit correct: alleen bij Platinum */}
            <div className="flex items-start gap-4 p-5 rounded-2xl" style={{
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.1)'
          }} data-id="element-28" data-mp="3vz61yobphvsg">
              <InfoIcon size={18} className="text-brand-arctic flex-shrink-0 mt-0.5" data-id="element-29" data-mp="dtkr9gd6tt7g" />
              <span className="text-white/70 text-sm leading-relaxed" data-id="element-30" data-mp="ikt46mdq7wpd">
                Overweeg je het Platinum-pakket? Daar hoort marktexclusiviteit
                bij. In het gesprek kijken we of jouw sector en regio daarvoor
                nog beschikbaar zijn.
              </span>
            </div>
          </motion.div>

          {/* RIGHT */}
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.55,
          delay: 0.15
        }} data-id="element-31" data-mp="2c30dkl19eto8">
            <div className="rounded-2xl overflow-hidden" style={{
            border: '1px solid rgba(255,255,255,0.1)',
            boxShadow: '0 24px 80px rgba(0,0,0,0.35)',
            background: 'rgba(13,18,32,0.98)'
          }} data-id="element-32" data-mp="1kd5ehwnlnpmd">
              {/* Header */}
              <div className="px-8 py-5 flex items-center justify-between" style={{
              background: 'rgba(255,255,255,0.04)',
              borderBottom: '1px solid rgba(255,255,255,0.06)'
            }} data-id="element-33" data-mp="1og20lxccm3v2">
                <div className="flex items-center gap-3" data-id="element-34" data-mp="11d81u4k0nzol">
                  {step === 'details' && <button type="button" onClick={() => {
                  setStep('pick');
                  setErrorMsg(null);
                }} className="w-8 h-8 rounded-lg flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-all" aria-label="Terug naar datum kiezen" data-id="element-35" data-mp="wfzs8scxp3yy">
                      <ArrowLeftIcon size={16} data-id="element-36" data-mp="dop0ya3aqp6j" />
                    </button>}
                  <CalendarIcon size={20} className="text-brand-arctic" data-id="element-37" data-mp="8p3sn928l4h5" />
                  <span className="text-white font-semibold text-base" data-id="element-38" data-mp="4oa6ol8ctzqw">
                    {step === 'pick' && 'Kies een moment'}
                    {step === 'details' && 'Jouw gegevens'}
                    {step === 'success' && 'Aanvraag ontvangen'}
                  </span>
                </div>
                {step !== 'success' && <div className="flex items-center gap-1.5" data-id="element-39" data-mp="3gz5170hczxkd">
                    <div className={`h-1.5 rounded-full transition-all ${step === 'pick' ? 'w-8 bg-brand-arctic' : 'w-3 bg-brand-arctic/40'}`} data-id="element-40" data-mp="3lyr9qwm8spmu" />
                    <div className={`h-1.5 rounded-full transition-all ${step === 'details' ? 'w-8 bg-brand-arctic' : 'w-3 bg-white/15'}`} data-id="element-41" data-mp="3qq9cnq7tt0ag" />
                  </div>}
              </div>

              <AnimatePresence mode="wait" data-id="element-42" data-mp="2y2n03sgmelbg">
                {/* STAP 1 */}
                {step === 'pick' && <motion.div key="pick" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.2
              }} className="p-7" data-id="element-43" data-mp="3329cy0sjdzjq">
                    <div className="flex items-center justify-between mb-5" data-id="element-44" data-mp="mdlzaa5b8zvn">
                      <button type="button" onClick={prevMonth} disabled={!canGoPrev} aria-label="Vorige maand" className="w-9 h-9 rounded-xl flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 disabled:opacity-20 disabled:cursor-not-allowed transition-all" data-id="element-45" data-mp="r91dj378cxsl">
                        <ChevronLeftIcon size={18} data-id="element-46" data-mp="2scu3dpkwex6v" />
                      </button>
                      <span className="text-white font-semibold" data-id="element-47" data-mp="2sar1o8advfmb">{MONTHS_NL[viewMonth]} {viewYear}</span>
                      <button type="button" onClick={nextMonth} aria-label="Volgende maand" className="w-9 h-9 rounded-xl flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-all" data-id="element-48" data-mp="24kwrjyhg855r">
                        <ChevronRightIcon size={18} data-id="element-49" data-mp="29krke7ksb4tg" />
                      </button>
                    </div>

                    <div className="grid grid-cols-7 gap-1.5 mb-2" data-id="element-50" data-mp="1lux93ug45obb">
                      {WEEKDAYS_NL.map(d => <div key={d} className="text-center text-white/30 text-xs font-semibold py-1" data-id="element-51" data-mp="1gvub2tyjwicu">{d}</div>)}
                    </div>

                    {calendarRows.map((row, ri) => <div key={ri} className="grid grid-cols-7 gap-1.5 mb-1.5" data-id="element-52" data-mp="3jklvbhjwnb1n" data-mpi={ri}>
                        {row.map((cell, ci) => {
                    if (!cell) return <div key={ci} className="h-10" data-id="element-53" data-mp="3nmguafed5cc9" />;
                    const avail = isAvailable(cell.date);
                    const selected = sameDay(selectedDate, cell.date);
                    const todayDot = isToday(cell.date);
                    return <button type="button" key={ci} onClick={() => selectDate(cell.date)} disabled={!avail} className={`h-10 rounded-lg text-sm font-semibold transition-colors duration-200 relative ${selected ? 'bg-brand-primary text-white' : avail ? 'text-white/80 hover:bg-white/10 hover:text-white bg-white/[0.03]' : 'text-white/15 cursor-not-allowed'}`} data-id="element-54" data-mp="64xniy1vz7z0" data-mpi={ci}>
                              {cell.day}
                              {todayDot && !selected && <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-brand-arctic" data-id="element-55" data-mp="36cwho9qpoonp" />}
                            </button>;
                  })}
                      </div>)}

                    <div className="mt-7 mb-6" data-id="element-56" data-mp="3u3se5k64481a">
                      <div className="text-white/50 text-xs mb-3 font-semibold uppercase tracking-wider" data-id="element-57" data-mp="3u2h9zj8r8y9n">
                        {selectedDate ? <>Tijden op <span className="text-white/80" data-id="element-58" data-mp="3l2c3dbbdy3wj">{formatShort(selectedDate)}</span></> : 'Kies eerst een datum'}
                      </div>
                      <div className="grid grid-cols-3 gap-2" data-id="element-59" data-mp="3g2zi2v05x4w2">
                        {TIME_SLOTS.map(t => {
                      const enabled = !!selectedDate;
                      const active = selectedTime === t;
                      return <button type="button" key={t} disabled={!enabled} onClick={() => enabled && setSelectedTime(t)} className={`py-2.5 rounded-xl text-sm font-semibold transition-colors duration-200 ${active ? 'bg-brand-primary text-white' : enabled ? 'text-white/75 hover:bg-white/10 hover:text-white' : 'text-white/20 cursor-not-allowed'}`} style={{
                        background: active ? undefined : 'rgba(255,255,255,0.04)',
                        border: active ? '1px solid #0066FF' : '1px solid rgba(255,255,255,0.08)'
                      }} data-id="element-60" data-mp="k7300pj09yvn">
                              {t}
                            </button>;
                    })}
                      </div>
                      <p className="text-white/35 text-xs mt-4 leading-relaxed" data-id="element-61" data-mp="3enwrhvlwwtg0">
                        Je kiest hier een voorkeursmoment. We bevestigen het
                        binnen één werkdag, of stellen een alternatief voor als
                        het net niet uitkomt.
                      </p>
                    </div>

                    <button type="button" onClick={() => {
                  if (selectedDate && selectedTime) {
                    setStep('details');
                    setErrorMsg(null);
                  }
                }} disabled={!selectedDate || !selectedTime} className="w-full py-4 rounded-xl font-semibold text-white flex items-center justify-center gap-3 transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed" style={{
                  background: selectedDate && selectedTime ? '#0052CC' : 'rgba(255,255,255,0.05)'
                }} data-id="element-62" data-mp="kwox4n1oi8o6">
                      Volgende: gegevens <ArrowRightIcon size={18} data-id="element-63" data-mp="1djt7l7jjjf7c" />
                    </button>
                  </motion.div>}

                {/* STAP 2 */}
                {step === 'details' && <motion.div key="details" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.2
              }} className="p-7" data-id="element-64" data-mp="1mlixua1os83j">
                    <div className="flex items-start gap-3 p-4 rounded-xl mb-6" style={{
                  background: 'rgba(0,102,255,0.08)',
                  border: '1px solid rgba(0,102,255,0.25)'
                }} data-id="element-65" data-mp="2b3jgd6edqenh">
                      <CalendarIcon size={18} className="text-brand-arctic flex-shrink-0 mt-0.5" data-id="element-66" data-mp="2164z54kmtlu4" />
                      <div className="flex-1" data-id="element-67" data-mp="1nf9xac8jw501">
                        <div className="text-white font-semibold text-sm" data-id="element-68" data-mp="1ndfdj190mixj">{selectedDate && formatLong(selectedDate)}</div>
                        <div className="text-white/60 text-xs mt-0.5" data-id="element-69" data-mp="1s7t851fusm15">{selectedTime} · 45 minuten · Online via Google Meet</div>
                      </div>
                      <button type="button" onClick={() => setStep('pick')} className="text-brand-arctic text-xs font-semibold hover:underline flex-shrink-0" data-id="element-70" data-mp="1igymnzaflqgr">Wijzigen</button>
                    </div>

                    <div className="space-y-3" data-id="element-71" data-mp="19e7a1w5tkonj">
                      <Field label="Naam" name="name" value={form.name} onChange={handleFormChange} placeholder="Voor- en achternaam" icon={UserIcon} required autoFocus data-id="element-72" data-mp="19h1sxj96q1eu" />
                      <Field label="E-mailadres" name="email" value={form.email} onChange={handleFormChange} placeholder="jij@bedrijf.nl" icon={MailIcon} required type="email" data-id="element-73" data-mp="1jyoz058y35hg" />
                      <Field label="Telefoonnummer" name="phone" value={form.phone} onChange={handleFormChange} placeholder="06 12345678" icon={PhoneIcon} required type="tel" data-id="element-74" data-mp="1awfovghl9e4l" />
                      <Field label="Bedrijf (optioneel)" name="company" value={form.company} onChange={handleFormChange} placeholder="Bedrijfsnaam" icon={BuildingIcon} data-id="element-75" data-mp="1175svpjmjbb7" />
                    </div>

                    {errorMsg && <div className="mt-4 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-xs font-medium" data-id="element-76" data-mp="ra9jziptcn64">{errorMsg}</div>}

                    <button type="button" onClick={handleSubmit} disabled={submitting} className="w-full mt-5 py-4 rounded-xl font-semibold text-white flex items-center justify-center gap-3 transition-opacity duration-200 disabled:opacity-60 disabled:cursor-not-allowed" style={{
                  background: '#0052CC'
                }} data-id="element-77" data-mp="n9zgqdteoauy">
                      {submitting ? <><Loader2Icon size={18} className="animate-spin" data-id="element-78" data-mp="32xmw4s2j478d" /> Versturen</> : <><CheckCircle2Icon size={20} data-id="element-79" data-mp="37uvdwrjrm993" /> Verstuur aanvraag</>}
                    </button>

                    <p className="text-center text-white/30 text-[11px] mt-3" data-id="element-80" data-mp="2b6ytfbl7itsl">
                      Je gaat akkoord met onze{' '}
                      <a href="https://noorddigital.nl/privacy" target="_blank" rel="noopener noreferrer" className="text-white/50 hover:text-white underline" data-id="element-81" data-mp="2f56ay8nhvz4u">privacyverklaring</a>.
                    </p>
                  </motion.div>}

                {/* STAP 3 */}
                {step === 'success' && <motion.div key="success" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                duration: 0.25
              }} className="p-10 text-center" data-id="element-82" data-mp="1wght5vkb69xa">
                    <div className="w-20 h-20 rounded-full bg-brand-primary/20 flex items-center justify-center mx-auto mb-6" data-id="element-83" data-mp="13us4p1lofs0a">
                      <CheckCircle2Icon size={40} className="text-brand-arctic" data-id="element-84" data-mp="17yrgj6w0i2nb" />
                    </div>
                    <h3 className="text-white font-bold text-2xl mb-3" data-id="element-85" data-mp="1hv2rntjv8mg6">Aanvraag ontvangen</h3>
                    <p className="text-white/60 text-sm leading-relaxed mb-2 max-w-sm mx-auto" data-id="element-86" data-mp="k7c8qylgut4o">
                      Je voorkeur staat genoteerd voor{' '}
                      <span className="text-white font-semibold" data-id="element-87" data-mp="p76wit9y32li">{selectedDate && formatLong(selectedDate)}</span> om{' '}
                      <span className="text-white font-semibold" data-id="element-88" data-mp="u3f9q14l0lq1">{selectedTime}</span>. We bevestigen binnen één werkdag.
                    </p>
                    <p className="text-white/50 text-sm mb-8" data-id="element-89" data-mp="1hcoqq4hg484">
                      Er is een bevestiging verstuurd naar <span className="text-white/80 font-semibold" data-id="element-90" data-mp="6fe9dfhk4blp">{form.email}</span>.
                    </p>
                    <button type="button" onClick={resetAll} className="text-brand-arctic text-sm font-semibold hover:underline" data-id="element-91" data-mp="3v0rd7zrtw09d">Nog een aanvraag doen</button>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}
type ComponentType<P = {}> = React.ComponentType<P>;
function Field({
  label,
  name,
  value,
  onChange,
  placeholder,
  type = 'text',
  required,
  icon: Icon,
  autoFocus
}: {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  type?: string;
  required?: boolean;
  icon?: ComponentType<{
    size?: number;
    className?: string;
  }>;
  autoFocus?: boolean;
}) {
  return <label className="block" data-id="element-92" data-mp="2xc7i3obpap8c">
      <span className="block text-white/60 text-xs font-semibold mb-1.5" data-id="element-93" data-mp="2sfh5jaz3ubro">
        {label}{required && <span className="text-brand-arctic ml-1" data-id="element-94" data-mp="2ocu88ujnzyn3">*</span>}
      </span>
      <div className="relative" data-id="element-95" data-mp="2ofq6yl3c6rcl">
        {Icon && <Icon size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30" data-id="element-96" data-mp="2dr7abg6sy9lu" />}
        <input type={type} name={name} value={value} onChange={onChange} placeholder={placeholder} autoFocus={autoFocus} required={required} className={`w-full ${Icon ? 'pl-10' : 'pl-4'} pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder:text-white/25 focus:outline-none focus:border-brand-arctic/60 focus:bg-white/[0.07] transition-all`} data-id="element-97" data-mp="2irlicy8eljjv" />
      </div>
    </label>;
}